'use client';

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, PropsWithChildren, Ref } from 'react';

type RevealOnScrollProps = PropsWithChildren<{
  className?: string;
  delayMs?: number;
  durationMs?: number;
  y?: number;
  transitionProperties?: string;
  style?: CSSProperties;
  ref?: Ref<HTMLDivElement>;
}>;

export function RevealOnScroll({
  children,
  className = '',
  delayMs = 0,
  durationMs = 900,
  y = 24,
  transitionProperties = 'opacity, transform',
  style,
  ref,
}: RevealOnScrollProps) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const element = innerRef.current;
    if (!element || revealed) return;

    const rect = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const isInViewport = rect.top < viewportHeight && rect.bottom > 0;

    if (isInViewport) {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setRevealed(true);
        observer.disconnect();
      },
      { root: null, threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [revealed]);

  return (
    <div
      ref={(node) => {
        innerRef.current = node;

        if (!ref) return;
        if (typeof ref === 'function') {
          ref(node);
          return;
        }

        ref.current = node;
      }}
      className={className}
      style={{
        opacity: revealed ? 1 : 0,
        transform: `translateY(${revealed ? 0 : y}px)`,
        transitionProperty: transitionProperties,
        transitionDuration: `${durationMs}ms`,
        transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
        transitionDelay: `${delayMs}ms`,
        willChange: revealed ? 'auto' : 'opacity, transform',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
